import React, { useEffect, useState, useMemo } from 'react';
import { Gift, ThumbsUp, ThumbsDown, MessageSquare, RefreshCw, Users, Download, AlertTriangle } from 'lucide-react';
import { Button } from './ui-common/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui-common/card';
import Spinner from './ui-common/spinner';
import { TooltipProvider } from './ui-common/tooltip';
import axiosInstance from './axiosInstance';

function exportToCSV(data, columns, filename) {
  if (!data || data.length === 0) return;

  // Filter out TOTAL row
  const rows = data.filter(row => row.email !== 'TOTAL' && row.name !== 'TOTAL');
  if (rows.length === 0) return;

  // Create header row
  const headers = columns.map(col => col.label);

  // Create data rows
  const csvRows = rows.map(row => {
    return columns.map(col => {
      let value = row[col.key];
      // Escape quotes and wrap in quotes if contains comma, newline, or quote
      if (value === null || value === undefined) {
        value = '';
      }
      value = String(value).replace(/"/g, '""');
      if (value.includes(',') || value.includes('\n') || value.includes('"')) {
        value = `"${value}"`;
      }
      return value;
    });
  });

  // Combine headers and rows
  const csvContent = [headers.join(','), ...csvRows.map(row => row.join(','))].join('\n');

  // Create and download the file
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

function formatTimestamp(ts) {
  if (!ts) return '-';
  try {
    const date = new Date(ts);
    const dateStr = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${dateStr} ${timeStr}`;
  } catch {
    return ts;
  }
}

function FeedbackTable({ title, description, data, columns, loading, icon: Icon, onExport }) {
  const totalRow = data?.find(row => row.email === 'TOTAL' || row.name === 'TOTAL');
  const dataRows = data?.filter(row => row.email !== 'TOTAL' && row.name !== 'TOTAL') || [];

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {Icon && <Icon className="h-5 w-5" />}
              {title}
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          {onExport && dataRows.length > 0 && (
            <Button variant="outline" size="sm" onClick={onExport}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size={40} thickness={4} className="border-foreground/30" />
          </div>
        ) : dataRows.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No feedback entries found
          </div>
        ) : (
          <>
            {/* Total count badge */}
            {totalRow && (
              <div className="mb-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
                <div className="flex items-center gap-2">
                  <Gift className="h-5 w-5 text-primary" />
                  <span className="font-semibold text-primary">
                    Total Entries: {totalRow.vote || totalRow.contact_email}
                  </span>
                </div>
              </div>
            )}

            {/* Table */}
            <div>
              <table className="w-full border-collapse table-fixed">
                <thead>
                  <tr className="border-b bg-muted/50">
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className="px-4 py-3 text-left text-sm font-semibold text-foreground"
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataRows.map((row, idx) => (
                    <tr
                      key={idx}
                      className="border-b hover:bg-muted/30 transition-colors"
                    >
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          className={`px-4 py-3 text-sm ${col.className || ''}`}
                        >
                          {col.render ? col.render(row[col.key], row) : (row[col.key] || '-')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function FeedbackPromotionPage() {
  const [conversationFeedback, setConversationFeedback] = useState([]);
  const [generalFeedback, setGeneralFeedback] = useState([]);
  const [loadingConversation, setLoadingConversation] = useState(true);
  const [loadingGeneral, setLoadingGeneral] = useState(true);
  const [error, setError] = useState(null);
  const [stagingOnly, setStagingOnly] = useState(false);

  const fetchData = async () => {
    setError(null);

    // Fetch conversation feedback
    setLoadingConversation(true);
    try {
      const res = await axiosInstance.get('/api/admin/feedback-promotion/conversation');
      setConversationFeedback(res.data || []);
    } catch (err) {
      console.error('Failed to fetch conversation feedback:', err);
      // Check if it's a 403 (staging-only feature)
      if (err.response?.status === 403) {
        setStagingOnly(true);
        setLoadingConversation(false);
        setLoadingGeneral(false);
        return;
      }
      setError('Failed to load conversation feedback');
    } finally {
      setLoadingConversation(false);
    }

    // Fetch general feedback
    setLoadingGeneral(true);
    try {
      const res = await axiosInstance.get('/api/admin/feedback-promotion/general');
      setGeneralFeedback(res.data || []);
    } catch (err) {
      console.error('Failed to fetch general feedback:', err);
      // Check if it's a 403 (staging-only feature)
      if (err.response?.status === 403) {
        setStagingOnly(true);
        setLoadingGeneral(false);
        return;
      }
      setError('Failed to load general feedback');
    } finally {
      setLoadingGeneral(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate combined stats - must be called before any conditional returns (React hooks rules)
  const stats = useMemo(() => {
    const conversationRows = conversationFeedback?.filter(row => row.email !== 'TOTAL') || [];
    const generalRows = generalFeedback?.filter(row => row.name !== 'TOTAL') || [];

    const totalFeedbacks = conversationRows.length + generalRows.length;

    // Collect all unique emails from both sources
    const allEmails = new Set();
    conversationRows.forEach(row => {
      if (row.email) allEmails.add(row.email.toLowerCase());
    });
    generalRows.forEach(row => {
      if (row.contact_email) allEmails.add(row.contact_email.toLowerCase());
    });

    return {
      totalFeedbacks,
      distinctUsers: allEmails.size,
      conversationCount: conversationRows.length,
      generalCount: generalRows.length,
    };
  }, [conversationFeedback, generalFeedback]);

  // Show staging-only message if detected from backend response
  if (stagingOnly) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            </div>
            <CardTitle>Feature Not Available</CardTitle>
            <CardDescription>
              The Feedback Promotion page is only available in the staging environment.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground mb-4">
              This feature is restricted to staging for testing purposes.
            </p>
            <Button variant="outline" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const conversationColumns = [
    {
      key: 'timestamp',
      label: 'Timestamp',
      render: (val) => formatTimestamp(val),
      className: 'whitespace-nowrap',
      width: '140px',
    },
    {
      key: 'email',
      label: 'Email',
      width: '250px',
      render: (val) => (
        <div>
          <div className="font-medium">{val || '-'}</div>
        </div>
      ),
    },
    {
      key: 'course',
      label: 'Course',
      width: '300px',
      render: (val) => (
        <div className="truncate">{val || '-'}</div>
      ),
    },
    {
      key: 'vote',
      label: 'Vote',
      width: '90px',
      render: (val) => (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
          val === 'positive' 
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
            : val === 'negative'
            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
        }`}>
          {val === 'positive' ? <ThumbsUp className="h-3 w-3" /> : val === 'negative' ? <ThumbsDown className="h-3 w-3" /> : null}
          {val || '-'}
        </span>
      ),
    },
    {
      key: 'feedback',
      label: 'Feedback Text',
      className: 'max-w-2xl',
      render: (val) => (
        <div className="max-w-2xl" style={{ wordBreak: 'break-word' }}>
          {val || '-'}
        </div>
      ),
    },
  ];

  const generalColumns = [
    {
      key: 'timestamp',
      label: 'Timestamp',
      render: (val) => formatTimestamp(val),
      className: 'whitespace-nowrap',
      width: '140px',
    },
    {
      key: 'name',
      label: 'Name',
      width: '150px',
      render: (val) => (
        <div className="font-medium">{val || '-'}</div>
      ),
    },
    {
      key: 'contact_email',
      label: 'Email',
      width: '200px',
      render: (val) => (
        <div className="truncate">{val || '-'}</div>
      ),
    },
    {
      key: 'courses',
      label: 'Courses',
      width: '300px',
      render: (val) => (
        <div className="text-sm" style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
          {val || '-'}
        </div>
      ),
    },
    {
      key: 'description',
      label: 'Feedback Description',
      className: 'max-w-2xl',
      render: (val) => (
        <div className="max-w-2xl" style={{ wordBreak: 'break-word' }}>
          {val || '-'}
        </div>
      ),
    },
  ];


  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="sticky top-0 z-10 border-b backdrop-blur-sm bg-muted/95">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
                  <Gift className="h-8 w-8 text-primary" />
                  Feedback Promotion Results
                </h1>
              </div>
              <Button variant="outline" onClick={fetchData} disabled={loadingConversation || loadingGeneral}>
                <RefreshCw className={`h-4 w-4 mr-2 ${(loadingConversation || loadingGeneral) ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
          {(loadingConversation || loadingGeneral) && (
            <div className="h-1 w-full bg-primary/30 animate-pulse" />
          )}
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto p-6">
          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
              {error}
            </div>
          )}

          {/* Stats Summary */}
          {!loadingConversation && !loadingGeneral && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border-purple-500/20">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-full">
                      <Gift className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Feedbacks</p>
                      <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{stats.totalFeedbacks}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-full">
                      <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Distinct Users</p>
                      <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.distinctUsers}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/20 rounded-full">
                      <ThumbsUp className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Conv. Feedback</p>
                      <p className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.conversationCount}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-500/10 to-amber-500/10 border-orange-500/20">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-500/20 rounded-full">
                      <MessageSquare className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">General Feedback</p>
                      <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{stats.generalCount}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* General Feedback Table */}
          <FeedbackTable
            title="General Feedback (Support Button)"
            description="Feedback submitted through the support button"
            data={generalFeedback}
            columns={generalColumns}
            loading={loadingGeneral}
            icon={MessageSquare}
            onExport={() => exportToCSV(generalFeedback, generalColumns, 'general_feedback')}
          />

          {/* Conversation Feedback Table */}
          <FeedbackTable
            title="Conversation Feedback (Like/Dislike)"
            description="Feedback submitted on individual chat responses"
            data={conversationFeedback}
            columns={conversationColumns}
            loading={loadingConversation}
            icon={ThumbsUp}
            onExport={() => exportToCSV(conversationFeedback, conversationColumns, 'conversation_feedback')}
          />
        </div>
      </div>
    </TooltipProvider>
  );
}
